import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Container,
  Button,
  Skeleton,
} from "@mui/material";
import { postRecord } from "services/services";
import { AuthContext } from "context/Authentication";

const API_GetAllCategories = "productcategories/getall";

export default function TopCategory() {
  const [topCategory, setTopCategory] = useState([]);
  const [visibleItems, setVisibleItems] = useState(8);
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const context = useContext(AuthContext);
  const isAudioBook =
    context.state?.companyInfo?.Product_Service_Type === "Product - Digital";

  useEffect(() => {
    let isMounted = true;

    const fetchTopCategories = async () => {
      try {
        const response = await postRecord(API_GetAllCategories, {});
        if (isMounted) {
          if (response.status === 200 && response.data) {
            const categories = response.data
              .filter((x) => x.Is_Published === "1" && x.Is_Featured === "1")
              .map((category) => ({
                image: encodeURI(JSON.parse(category.Category_Media_Files)[0]),
                name: category.Category_Name,
                description: category.Short_Description,
                longdescription: category.Long_Description,
                categoryType: category.Category_Type, // Include category type
              }));
            setTopCategory(categories);
          }
          setLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching categories:", error);
          setLoading(false);
        }
      }
    };

    fetchTopCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleToggleView = () => {
    setShowMore(!showMore);
    setVisibleItems(showMore ? 8 : topCategory.length);
  };

  const handleCategoryClick = (category) => {
    const formattedCategoryName = category.name
      .toLowerCase()
      .replace(/ /g, "-");

    // Redirect based on category type
    if (category.categoryType === "Service") {
      navigate(`/ServiceList/${formattedCategoryName}`);
    } else {
      // Default to ProductList for 'Product' type or if type is missing
      navigate(`/ProductList/${formattedCategoryName}`);
    }
  };

  return (
    <React.Fragment>
      <Container maxWidth="xl">
        <Box
          sx={{
            position: "relative",
            py: 3,
            px: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
            {isAudioBook ? "Top Genres" : "Top Category"}
          </Typography>
          <Box
            sx={{
              width: "80px",
              height: "2px",
              backgroundColor: "primary.main",
              mx: "auto",
              mb: 2,
            }}
          />
          <Grid
            container
            spacing={1}
            justifyContent="center"
            alignItems="stretch"
          >
            {loading
              ? // Skeleton cards while loading
                Array.from(new Array(8)).map((_, index) => (
                  <Grid
                    item
                    xs={isAudioBook ? 6 : 12}
                    sm={isAudioBook ? 4 : 6}
                    md={isAudioBook ? 3 : 4}
                    lg={isAudioBook ? 2 : 3}
                    xl={isAudioBook ? 2 : 3}
                    key={index}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <Card
                      sx={{
                        height: isAudioBook ? "300px" : "400px",
                        borderRadius: "8px",
                        width: "100%",
                        maxWidth: isAudioBook ? "280px" : "none",
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="70%"
                        animation="wave"
                      />
                      <CardContent>
                        <Skeleton animation="wave" height={30} width="80%" />
                        <Skeleton animation="wave" height={20} width="60%" />
                        <Skeleton animation="wave" height={20} width="40%" />
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              : // Actual content after loading
                topCategory.slice(0, visibleItems).map((item, index) => (
                  <Grid
                    item
                    xs={isAudioBook ? 6 : 12}
                    sm={isAudioBook ? 4 : 6}
                    md={isAudioBook ? 3 : 4}
                    lg={isAudioBook ? 2 : 3}
                    xl={isAudioBook ? 2 : 3}
                    key={index}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <Card
                      sx={{
                        position: "relative",
                        overflow: "hidden",
                        height: isAudioBook ? "300px" : "400px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        width: "100%",
                        maxWidth: isAudioBook ? "280px" : "none",
                        "& *": {
                          transition: "all 0.5s ease-in-out",
                        },
                        "&:hover .card-image": {
                          transform: "scale(1.5)",
                        },
                      }}
                      onClick={() => handleCategoryClick(item)}
                    >
                      <CardMedia
                        className="card-image"
                        component="img"
                        height="100%"
                        image={`${process.env.BASEURL}${item.image}`}
                        sx={{ transition: "transform 2s ease-in-out" }}
                        alt={item.name}
                      />
                      <CardContent
                        className="card-content"
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          width: "100%",
                          height: "30%", // Fixed initial height
                          backgroundColor: "rgba(0, 0, 0, 0.6)",
                          color: "#fff",
                          textAlign: "left",
                          py: 1,
                          transition: "all 0.6s ease-in-out",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-end", // Align content at the bottom initially
                          "&:hover": {
                            height: "100%", // Expand to full card height on hover
                            bottom: 0, // Position from the top instead of bottom
                            bottom: "auto",
                            justifyContent: "flex-start", // Align content at the top when expanded
                          },
                          ".MuiCard-root:hover &": {
                            height: "100%",
                            bottom: 0,
                            // bottom: "auto",
                            justifyContent: "flex-start",
                          },
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          {item.name}
                        </Typography>
                        <Typography variant="subtitle2">
                          {item.description.length > 40
                            ? item.description.slice(0, 40) + "..."
                            : item.description}
                        </Typography>
                        <Typography
                          variant="body2"
                          className="hidden-desc"
                          sx={{
                            marginTop: "12px",
                            opacity: 0,
                            maxHeight: 0,
                            overflow: "hidden",
                            transition:
                              "opacity 0.5s ease-in-out 0.1s, max-height 0.5s ease-in-out",
                            ".MuiCard-root:hover &": {
                              opacity: 1,
                              maxHeight: "200px",
                            },
                          }}
                        >
                          {item.longdescription.length > 150
                            ? item.longdescription.slice(0, 150) + "..."
                            : item.longdescription}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
          </Grid>
        </Box>
      </Container>
    </React.Fragment>
  );
}
